import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AUDIT_EPSILON,
  D,
  fmtAmt,
  isCcyCode,
  type AuditChatRow,
  type BotTxn,
  type CcyCode,
} from '@wave/shared';
import { hasRight, useAuthStore } from '../../auth/store';
import { useArchiveOps, useAuditHistory, useBotTransactions, useOrdersList } from '../../api/hooks';
import { useCreateAuditAck, useReleaseAuditAck } from '../../api/mutations';
import { Modal } from '../Modal';
import { Chart } from '../Chart';
import { STATUS_META, fmtDiverged } from '../../pages/AuditPage';
import { botTxnOpsKey, counterpartyCell, orderRefCell } from './auditChatOps';
import {
  btnGhostSm,
  btnPrimarySm,
  btnSecondarySm,
  cardL2,
  inputCls,
  subSectionTitle,
  tableCell,
  tableCellNum,
  tableCls,
  tableHead,
  tableHeadCell,
  tableHeadCellRight,
  tableRow,
  textareaCls,
} from '../../lib/styles';

/** Форматирует сумму движения бота: известная валюта → fmtAmt, иначе как есть. */
function fmtTxnAmt(amount: string, ccy: string): string {
  return isCcyCode(ccy) ? fmtAmt(amount, ccy) : `${amount} ${ccy}`;
}

/** t.me-ссылка на супергруппу: -100XXXXXXXXXX → https://t.me/c/XXXXXXXXXX. */
function tmeLink(chatId: string): string | null {
  if (!chatId.startsWith('-100')) return null;
  return `https://t.me/c/${chatId.slice(4)}`;
}

export function AuditChatModal({ chat, onClose }: { chat: AuditChatRow; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const rights = useAuthStore((s) => s.rights);
  const canAck = hasRight(rights, 'audit.ack');

  const mismatchCcys = chat.rows.filter((r) => r.mismatch).map((r) => r.ccy);
  const [chartCcy, setChartCcy] = useState<CcyCode | null>(mismatchCcys[0] ?? chat.rows[0]?.ccy ?? null);
  const { data: history } = useAuditHistory(chat.chatId, chartCcy, '30d');

  const [ackFor, setAckFor] = useState<CcyCode | null>(null);
  const [comment, setComment] = useState('');
  const createAck = useCreateAuditAck();
  const releaseAck = useReleaseAuditAck();

  // Открытые заявки контрагента (new/progress) — куда смотреть в системе.
  // limit 500: открытые заявки почти всегда среди свежих; серверный фильтр по
  // контрагенту — follow-up (см. ledger)
  const { data: orders } = useOrdersList('all', undefined, 500);
  const openOrders = useMemo(() => {
    if (!chat.accountCode || !orders) return [];
    return orders.items.filter(
      (o) =>
        (o.client === chat.accountCode || o.executor === chat.accountCode) &&
        (o.status === 'new' || o.status === 'progress'),
    );
  }, [orders, chat.accountCode]);

  const meta = STATUS_META[chat.status];
  const link = tmeLink(chat.chatId);
  const worst = chat.rows.filter((r) => r.mismatch && !r.ack)[0];

  const chartPoints = (history?.points ?? []).map((p) => ({
    date: p.takenAt,
    value: Number(D(p.delta).toDecimalPlaces(2).toString()),
  }));

  return (
    <Modal title={chat.title} onClose={onClose} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className={`font-medium ${meta.cls}`}>
            {meta.icon} {meta.label}
          </span>
          <span className="text-text2">{chat.accountCode ?? 'контрагент не привязан'}</span>
          {chat.inverted && <span className="text-text2">⇄ инвертированная модель</span>}
          <span className="ml-auto flex gap-2">
            {link && (
              <a href={link} target="_blank" rel="noreferrer" className={btnSecondarySm}>
                Открыть чат в Telegram ↗
              </a>
            )}
            {chat.status === 'unmapped' && (
              <button type="button" className={btnPrimarySm} onClick={() => navigate('/admin/chats')}>
                Привязать контрагента
              </button>
            )}
          </span>
        </div>

        {worst && (
          <div className="rounded-md border border-[var(--accent)] bg-[var(--accent-bg)] px-4 py-3 text-sm text-text">
            Ищите в чате операцию на ≈{' '}
            <b className="font-mono">
              {D(worst.delta).isNegative() ? '' : '+'}
              {fmtAmt(worst.delta, worst.ccy)}
            </b>
            {worst.divergedBefore && <> — период: {fmtDiverged(worst.divergedAfter, worst.divergedBefore)}</>}
          </div>
        )}

        <div>
          <div className={subSectionTitle}>Сверка по валютам</div>
          <table className={`${tableCls} mt-2`}>
            <thead className={tableHead}>
              <tr>
                <th className={tableHeadCell}>Валюта</th>
                <th className={tableHeadCellRight}>Бот</th>
                <th className={tableHeadCellRight}>Система</th>
                <th className={tableHeadCellRight}>Δ</th>
                <th className={tableHeadCell}>Ack</th>
              </tr>
            </thead>
            <tbody>
              {chat.rows.map((r) => (
                <tr key={r.ccy} className={tableRow}>
                  <td className={`${tableCell} font-medium`}>{r.ccy}</td>
                  <td className={tableCellNum}>{fmtAmt(r.external, r.ccy)}</td>
                  <td className={tableCellNum}>{fmtAmt(r.internal, r.ccy)}</td>
                  <td
                    className={`${tableCellNum} font-semibold ${
                      !r.mismatch ? 'text-text2' : D(r.delta).isNegative() ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {D(r.delta).isNegative() ? '' : '+'}
                    {fmtAmt(r.delta, r.ccy)}
                  </td>
                  <td className={tableCell}>
                    {r.ack ? (
                      <span className="flex items-center gap-2 text-xs text-text2">
                        💬 {r.ack.comment}
                        <span className="text-text-subtle">({r.ack.createdBy ?? '—'})</span>
                        {canAck && (
                          <button
                            type="button"
                            className={btnGhostSm}
                            onClick={() => releaseAck.mutate(r.ack!.id)}
                          >
                            Снять
                          </button>
                        )}
                      </span>
                    ) : r.mismatch && canAck ? (
                      <button type="button" className={btnGhostSm} onClick={() => setAckFor(r.ccy)}>
                        Пометить объяснённым
                      </button>
                    ) : (
                      <span className="text-text-subtle">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {ackFor && (
          <div className={cardL2}>
            <div className={subSectionTitle}>Объяснить расхождение {ackFor}</div>
            <textarea
              className={`${textareaCls} mt-2 w-full`}
              rows={2}
              maxLength={500}
              placeholder="Почему это расхождение — норма (обязательно)…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className={btnPrimarySm}
                disabled={!comment.trim() || createAck.isPending}
                onClick={() => {
                  const row = chat.rows.find((r) => r.ccy === ackFor)!;
                  createAck.mutate(
                    { chatId: chat.chatId, ccy: ackFor, delta: row.delta, comment: comment.trim() },
                    { onSuccess: () => { setAckFor(null); setComment(''); } },
                  );
                }}
              >
                Сохранить
              </button>
              <button type="button" className={btnGhostSm} onClick={() => setAckFor(null)}>
                Отмена
              </button>
            </div>
            {createAck.isError && (
              <div className="mt-2 text-xs text-danger">
                {(createAck.error as Error)?.message || 'Не удалось: дельта могла измениться — обновите сверку.'}
              </div>
            )}
          </div>
        )}

        {chartCcy && (
          <div>
            <div className="flex items-center gap-2">
              <div className={subSectionTitle}>История Δ (30 дней)</div>
              <div className="ml-auto flex gap-1">
                {chat.rows.map((r) => (
                  <button
                    key={r.ccy}
                    type="button"
                    className={`rounded px-2 py-0.5 text-xs ${
                      chartCcy === r.ccy ? 'bg-accent text-white' : 'bg-bg3 text-text2'
                    }`}
                    onClick={() => setChartCcy(r.ccy)}
                  >
                    {r.ccy}
                  </button>
                ))}
              </div>
            </div>
            {chartPoints.length > 1 ? (
              <div className="mt-2">
                <Chart
                  points={chartPoints}
                  color="danger"
                  height={180}
                  formatValue={(v) => `${v} ${chartCcy}`}
                  formatDate={(iso) =>
                    new Date(iso).toLocaleString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }
                />
              </div>
            ) : (
              <div className="mt-2 text-sm text-text2">Недостаточно данных для графика (нужно ≥2 сверок).</div>
            )}
          </div>
        )}

        <BotTxnsSection chat={chat} defaultDate={worst?.divergedBefore?.slice(0, 10) ?? null} />

        {chat.accountCode && (
          <div>
            <div className={subSectionTitle}>Открытые заявки контрагента ({openOrders.length})</div>
            {openOrders.length === 0 ? (
              <div className="mt-2 text-sm text-text2">Открытых заявок нет.</div>
            ) : (
              <ul className="mt-2 space-y-1">
                {openOrders.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="text-sm text-accent hover:underline"
                      onClick={() =>
                        navigate(`/orders/${o.id}`, { state: { backgroundLocation: location } })
                      }
                    >
                      {o.id}
                    </button>
                    <span className="ml-2 text-xs text-text2">{o.date}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function BotTxnsSection({ chat, defaultDate }: { chat: AuditChatRow; defaultDate: string | null }) {
  const [date, setDate] = useState<string | null>(defaultDate);
  const { data, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useBotTransactions(
    chat.chatId,
    date,
  );
  // Обогащение «заявка»/«встречный чат» — джойн делает сервер (GET /archive/ops,
  // спека §5); без выбранной даты сервер отдаёт пустое enrichment (задача 19).
  const { data: ops } = useArchiveOps(chat.chatId, date ?? undefined);

  const first = data?.pages[0];
  if (!first || !first.configured) return null; // журнал не настроен — секции нет

  const items = data!.pages.flatMap((p) => p.items);
  const total = first.total;

  // Дельты с расхождением по валютам — для подсветки кандидатов.
  const deltaByCcy = new Map(
    chat.rows.filter((r) => r.mismatch).map((r) => [r.ccy as string, D(r.delta)]),
  );
  const eps = D(AUDIT_EPSILON);
  const isCandidate = (t: BotTxn) => {
    const delta = deltaByCcy.get(t.ccy);
    return !!delta && D(t.amount).minus(delta).abs().lte(eps);
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className={subSectionTitle}>Движения в боте {total > 0 && `(${total})`}</div>
        <input
          type="date"
          className={`${inputCls} ml-auto w-40`}
          value={date ?? ''}
          onChange={(e) => setDate(e.target.value || null)}
        />
        {date && (
          <button type="button" className={btnGhostSm} onClick={() => setDate(null)}>
            Все даты
          </button>
        )}
      </div>
      {first.error && <div className="mt-2 text-xs text-danger">Журнал недоступен: {first.error}</div>}
      {error && (
        <div className="mt-2 text-xs text-danger">
          Не удалось загрузить журнал: {(error as Error).message}
        </div>
      )}
      {items.length === 0 && !first.error && <div className="mt-2 text-sm text-text2">Движений нет.</div>}
      {items.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {items.map((t, i) => {
            const candidate = isCandidate(t);
            const entry = ops?.enrichment[botTxnOpsKey(t)];
            const orderCell = orderRefCell(date, entry);
            const counterCell = counterpartyCell(date, entry);
            return (
              <li
                key={`${t.dateTime}-${i}`}
                className={`flex items-center gap-3 rounded-md px-2 py-1 text-sm ${
                  candidate ? 'border border-[var(--accent)] bg-[var(--accent-bg)]' : ''
                }`}
              >
                <span className="w-32 shrink-0 font-mono text-xs text-text2">
                  {t.dateTime.replace('T', ' ').slice(0, 16)}
                </span>
                <span className="flex-1 truncate text-text2">{t.userName ?? '—'}</span>
                <span className="w-20 shrink-0 truncate text-xs text-text2" title={orderCell.hint}>
                  {orderCell.text}
                </span>
                <span className="w-32 shrink-0 truncate text-xs text-text2" title={counterCell.hint}>
                  {counterCell.text}
                </span>
                {candidate && <span className="text-xs font-semibold text-accent">≈ Δ</span>}
                <span
                  className={`font-mono tabular-nums ${
                    D(t.amount).isNegative() ? 'text-success' : 'text-danger'
                  }`}
                >
                  {D(t.amount).isNegative() ? '' : '+'}
                  {fmtTxnAmt(t.amount, t.ccy)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {hasNextPage && (
        <button
          type="button"
          className={`${btnGhostSm} mt-2`}
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? 'Загружаем…' : `Показать ещё (${items.length} из ${total})`}
        </button>
      )}
    </div>
  );
}
